import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { formatoPorcentaje } from '@/lib/utils/formato-precio';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import {
  tariffCatalogService,
  type ResultadoBusqueda,
} from '@/features/tariff-assistant/services/tariff-catalog-service';
import type { PartidaDeProducto } from '../../types/comercia.types';
import { busquedasCortas, puntuar, type Pistas, type Puntuada } from '../../utils/coincidencia';

const EJEMPLOS = ['Notebook 14 pulgadas', 'Monitor LED 24 pulgadas', 'Panel solar 550 W'];

/**
 * El asistente arancelario de Comercia (maqueta de Melanie): «la persona pone qué
 * quiere importar y le recomienda: esto es lo que más te sugiero, pero estos son
 * los demás». Mucho más simple que el asistente completo, que sigue en su página
 * para quien lo necesite.
 *
 * Busca con el mismo buscador del asistente completo (sinónimos, aperturas y, si
 * está configurada, la IA que traduce el producto al vocabulario del arancel).
 */
export function AsistenteArancelario({
  onElegir,
}: {
  /** Abre «Agregar producto» con la descripción y la partida elegidas. */
  onElegir: (descripcion: string, partida: PartidaDeProducto) => void;
}) {
  const [consulta, setConsulta] = useState('');
  const [buscada, setBuscada] = useState('');
  const [sugeridas, setSugeridas] = useState<Array<Puntuada<ResultadoBusqueda>> | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [afinando, setAfinando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** La búsqueda vigente: si la persona busca otra cosa, lo que llega tarde se descarta. */
  const vigente = useRef(0);

  const sugerir = async (texto = consulta) => {
    const q = texto.trim();
    if (q.length < 3) return;
    const esta = ++vigente.current;
    setBuscando(true);
    setAfinando(false);
    setError(null);
    try {
      const r = await tariffCatalogService.buscar({ q, limit: 10 });
      if (esta !== vigente.current) return;
      const pistas: Pistas = {
        concepto: r.interpretacion?.concepto,
        terminosLegales: r.interpretacion?.terminosLegales,
      };
      const candidatas = new Map<string, ResultadoBusqueda>();
      const sumar = (lista: ResultadoBusqueda[] | undefined) => {
        for (const p of lista ?? []) {
          if (p.importProhibited !== true && !candidatas.has(p.id)) candidatas.set(p.id, p);
        }
      };
      sumar(r.resultados);
      setSugeridas(puntuar(q, [...candidatas.values()], 3, pistas));
      setBuscada(q);
      setBuscando(false);

      /*
        Afinar: buscar también los términos del arancel que propuso la IA, cortos,
        para que el buscador los resuelva por texto (ver `busquedasCortas`). Lo
        primero ya está en pantalla; esto lo reordena cuando llega.
      */
      const cortas = busquedasCortas(q, pistas);
      if (cortas.length === 0) return;
      setAfinando(true);
      const extra = await Promise.allSettled(
        cortas.map((c) => tariffCatalogService.buscar({ q: c.q, chapter: c.capitulo, limit: 10 }))
      );
      if (esta !== vigente.current) return;
      for (const e of extra) if (e.status === 'fulfilled') sumar(e.value.resultados);
      setSugeridas(puntuar(q, [...candidatas.values()], 3, pistas));
    } catch (e) {
      if (esta === vigente.current)
        setError(mensajeDeError(e, 'No se pudo consultar el arancel. Vuelve a intentar.'));
    } finally {
      if (esta === vigente.current) {
        setBuscando(false);
        setAfinando(false);
      }
    }
  };

  const ga = (p: ResultadoBusqueda) => Number(p.gaRate) * 100;

  return (
    <div className="space-y-6">
      <section className="rounded-tarjeta border bg-card px-6 py-6 shadow-sm">
        <h2 className="font-titulo text-xl font-semibold">¿Qué vas a importar?</h2>
        <p className="mt-1 text-[15px] text-muted-foreground">
          Escríbelo como lo dirías: qué es, para qué sirve, de qué está hecho. Prixia te sugiere la
          partida arancelaria.
        </p>
        <form
          className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-stretch"
          onSubmit={(e) => {
            e.preventDefault();
            void sugerir();
          }}
        >
          <label className="flex-1">
            <span className="sr-only">Qué vas a importar</span>
            <textarea
              value={consulta}
              onChange={(e) => setConsulta(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void sugerir();
                }
              }}
              rows={2}
              placeholder="Aire acondicionado split inverter de 12.000 BTU, para pared, solo frío"
              // La altura de la maqueta (84 px en una captura al 125 %).
              className="h-full min-h-[67px] w-full resize-y rounded-2xl border-2 border-orange-700/70 bg-campo px-5 py-3 text-base placeholder:text-muted-foreground focus-visible:border-orange-700 focus-visible:outline-none"
            />
          </label>
          <Button
            type="submit"
            variant="degradado"
            className="h-auto min-h-[52px] rounded-full px-7 text-base lg:min-w-[170px]"
            disabled={buscando || consulta.trim().length < 3}
          >
            {buscando ? (
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <Sparkles className="h-5 w-5" aria-hidden="true" />
            )}
            Sugerir partida
          </Button>
        </form>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>Prueba con:</span>
          {EJEMPLOS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => {
                setConsulta(e);
                void sugerir(e);
              }}
              className="rounded-full border bg-card px-3.5 py-1.5 text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {e}
            </button>
          ))}
        </div>
      </section>

      {error && (
        <p role="alert" className="rounded-xl bg-bloqueo-suave px-4 py-3 text-sm text-bloqueo">
          {error}
        </p>
      )}

      {sugeridas && (
        <section aria-live="polite" aria-label="Partidas sugeridas" className="space-y-4">
          <h2 className="flex flex-wrap items-baseline gap-3">
            <span className="font-titulo text-xl font-semibold">
              {sugeridas.length === 1
                ? '1 partida sugerida'
                : `${sugeridas.length} partidas sugeridas`}
            </span>
            {sugeridas.length > 1 && (
              <span className="text-sm text-muted-foreground">ordenadas por coincidencia</span>
            )}
            {afinando && (
              <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                Afinando con el arancel…
              </span>
            )}
          </h2>
          {sugeridas.length === 0 ? (
            <p className="rounded-tarjeta border border-dashed px-5 py-8 text-center text-sm text-muted-foreground">
              No encontramos una partida para «{buscada}». Prueba con otras palabras: qué es y para
              qué sirve.
            </p>
          ) : (
            sugeridas.map((s, i) => {
              const recomendada = i === 0;
              return (
                <article
                  key={s.partida.id}
                  className={cn(
                    'flex flex-col gap-4 rounded-tarjeta border bg-card px-6 py-5 shadow-sm sm:flex-row sm:items-center',
                    recomendada && 'border-2 border-orange-700/80'
                  )}
                >
                  <div className="w-24 shrink-0 text-center">
                    <p
                      className={cn(
                        'font-titulo text-[28px] font-semibold leading-none tabular-nums',
                        recomendada ? 'text-ok' : 'text-muted-foreground'
                      )}
                    >
                      {formatoPorcentaje(s.coincidencia, { decimales: 0 })}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">coincidencia</p>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-base font-bold">{s.partida.code}</p>
                    <p
                      className="mt-0.5 line-clamp-2 text-[15px]"
                      title={s.partida.fullContext ?? undefined}
                    >
                      {s.partida.description}
                      {padreQueExplica(s.partida) && (
                        <span className="text-muted-foreground">
                          {' '}
                          · {padreQueExplica(s.partida)}
                        </span>
                      )}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {s.coincide.length > 0 ? (
                        <>coincide: {s.coincide.join(' · ')} · </>
                      ) : s.coincideIa && s.coincideIa.length > 0 ? (
                        <>por lo que entendió la IA: {s.coincideIa.slice(0, 4).join(' · ')} · </>
                      ) : (
                        <>sin palabras en común: revisa la descripción · </>
                      )}
                      arancel{' '}
                      <strong className="text-foreground">
                        {formatoPorcentaje(ga(s.partida), { decimalesMaximos: 2 })}
                      </strong>
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant={recomendada ? 'degradado' : 'outline'}
                    className="shrink-0 rounded-full px-6"
                    onClick={() =>
                      onElegir(buscada, {
                        id: s.partida.id,
                        codigo: s.partida.code,
                        descripcion: s.partida.description,
                        ga: ga(s.partida),
                      })
                    }
                  >
                    {recomendada && <Check className="h-4 w-4" aria-hidden="true" />}
                    {recomendada ? 'Recomendada' : 'Elegir'}
                  </Button>
                </article>
              );
            })
          )}
          <p className="text-sm text-muted-foreground">
            La coincidencia compara lo que escribiste con el texto oficial de cada partida. ¿Ninguna
            te convence?{' '}
            <Link to="/tariff-assistant" className="font-semibold text-primary hover:underline">
              Abre el arancel completo
            </Link>
            .
          </p>
        </section>
      )}
    </div>
  );
}

/**
 * «Los demás» no dice nada solo: se acompaña del nivel de arriba en el arancel
 * («De los tipos diseñados para ser montados sobre una ventana, pared…»). Sólo
 * cuando la descripción es corta; si ya se explica, no se agrega nada.
 */
function padreQueExplica(p: ResultadoBusqueda): string | null {
  if (!p.fullContext || p.description.length >= 40) return null;
  const niveles = p.fullContext
    .split('>')
    .map((n) => n.trim().replace(/:$/, ''))
    .filter((n) => n && n !== p.description.trim());
  return niveles[niveles.length - 1] ?? null;
}
